import { useEffect, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { effectiveUiZoom } from "./ui-scale";

export type ContextTarget =
  | { kind: "desktop" }
  | { kind: "dock-app"; appId: string; label: string }
  | { kind: "window"; windowId: string; appId: string; title: string }
  | { kind: "menubar" }
  | { kind: "app"; appId?: string; detail?: string };

export type ContextMenuItem =
  | {
      id: string;
      label: string;
      icon?: LucideIcon | string;
      danger?: boolean;
      disabled?: boolean;
      shortcut?: string;
      action: () => void;
    }
  | { id: string; separator: true };

export type ContextMenuState = {
  x: number;
  y: number;
  target: ContextTarget;
  items: ContextMenuItem[];
} | null;

type Listener = () => void;

class ContextMenuStore {
  menu: ContextMenuState = null;
  private listeners = new Set<Listener>();

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  private emit() {
    for (const fn of this.listeners) fn();
  }

  open(x: number, y: number, target: ContextTarget, items: ContextMenuItem[]) {
    // body.style.zoom (UI scale) vs MouseEvent clientX/Y (unscaled).
    // Menus are position:fixed inside the zoomed body - divide coords.
    const scale = effectiveUiZoom();
    const lx = x / scale;
    const ly = y / scale;
    const pad = 8;
    const approxW = 240;
    const approxH = Math.min(400, 16 + items.length * 34);
    const viewW = window.innerWidth / scale;
    const viewH = window.innerHeight / scale;
    const left = Math.max(pad, Math.min(lx, viewW - approxW - pad));
    const top = Math.max(pad, Math.min(ly, viewH - approxH - pad));
    this.menu = { x: left, y: top, target, items };
    this.emit();
  }

  close() {
    if (!this.menu) return;
    this.menu = null;
    this.emit();
  }
}

export const contextMenuStore = new ContextMenuStore();

export function useContextMenu(): ContextMenuState {
  const [menu, setMenu] = useState(contextMenuStore.menu);
  useEffect(
    () => contextMenuStore.subscribe(() => setMenu(contextMenuStore.menu)),
    []
  );
  return menu;
}

